import { pricingPlanMessages } from 'src/features/pricingPlan/utils/messages';
import { ICON_MAP } from 'src/utils/constants';
import { BadgeOption } from 'src/registry/types/badges';
import {
    BILLING_TYPE,
    CALCULATION_TYPE,
    CAPPING,
    DAY_OF_WEEK,
    FREE_TIME_TYPE,
    PAYMENT_TYPE,
    PRICING_PLAN_STATUS,
    TICK_TYPE, USED_IN_TYPE
} from 'src/features/pricingPlan/utils/types';
import { CURRENCY } from 'src/types';
import { workingHoursMessages } from 'src/features/workingHours/utils/messages';
import { siteMessages } from 'src/features/sites/utils/messages';
import { zoneMessages } from 'src/features/zone/utils/messages';
import { gatewayMessages } from 'src/features/gateway/utils/messages';


export const pricingBillingBadgeOptions: Record<BILLING_TYPE, BadgeOption> = {
    [BILLING_TYPE.POST]: {
        label: pricingPlanMessages.postBilling,
        icon: ICON_MAP.CHECK,
        theme: 'green',
    },
    [BILLING_TYPE.PRE]: {
        label: pricingPlanMessages.preBilling,
        icon: ICON_MAP.X_MARK,
        theme: 'red',
    },
};

export const pricingCalculationBadgeOptions: Record<CALCULATION_TYPE, BadgeOption> = {
    [CALCULATION_TYPE.PER_FIXED_TICK]: {
        label: pricingPlanMessages.fixedTick,
        icon: ICON_MAP.CHECK,
        theme: 'green',
    },
    [CALCULATION_TYPE.PER_PERIODS_TOTAL]: {
        label: pricingPlanMessages.totalPeriods,
        icon: ICON_MAP.X_MARK,
        theme: 'red',
    },
    [CALCULATION_TYPE.PER_PERIODS_COMPOUND]: {
        label: pricingPlanMessages.compoundPeriods,
        icon: ICON_MAP.CHECK,
        theme: 'blue',
    },
    [CALCULATION_TYPE.FIXED_PRICE]: {
        label: pricingPlanMessages.fixedPrice,
        icon: ICON_MAP.CHECK,
        theme: 'orange',
    },
};

export const freeTimeTypeMapping: Record<FREE_TIME_TYPE, { id: string; defaultMessage: string }> = {
    [FREE_TIME_TYPE.CAPPED]: pricingPlanMessages.capping,
    [FREE_TIME_TYPE.EVERY_VISIT]: pricingPlanMessages.everyVisit,
};

export const cappingTranslationMapping = {
    [CAPPING.NONE]: pricingPlanMessages.none,
    [CAPPING.ONE_TIME]: pricingPlanMessages.oneTime,
    [CAPPING.REPEATING]: pricingPlanMessages.repeating,
};

export const tickTypeTranslationMapping: Record<TICK_TYPE, { id: string; defaultMessage: string }> = {
    [TICK_TYPE.MINUTE]: pricingPlanMessages.perMinute,
    [TICK_TYPE.HOUR]: pricingPlanMessages.hourly,
    [TICK_TYPE.DAY]: pricingPlanMessages.daily,
    [TICK_TYPE.WEEK]: pricingPlanMessages.weekly,
    [TICK_TYPE.MONTH]: pricingPlanMessages.monthly,
};

export const pricingStatusBadgeOptions: Record<PRICING_PLAN_STATUS, BadgeOption> = {
    [PRICING_PLAN_STATUS.DRAFT]: {
        label: pricingPlanMessages.draft,
        icon: ICON_MAP.WAVE_PULSE,
        theme: 'yellow',
    },
    [PRICING_PLAN_STATUS.PUBLISHED]: {
        label: pricingPlanMessages.published,
        icon: ICON_MAP.WAVE_PULSE,
        theme: 'green',
    },
};

export const BILLING_TYPE_MAP = {
    [BILLING_TYPE.POST]: pricingPlanMessages.postBilling,
    [BILLING_TYPE.PRE]: pricingPlanMessages.preBilling,
};

export const CALCULATION_TYPE_MAP = {
    [CALCULATION_TYPE.PER_PERIODS_COMPOUND]: pricingPlanMessages.compoundPeriods,
    [CALCULATION_TYPE.PER_FIXED_TICK]: pricingPlanMessages.fixedTick,
    [CALCULATION_TYPE.PER_PERIODS_TOTAL]: pricingPlanMessages.totalPeriods,
    [CALCULATION_TYPE.FIXED_PRICE]: pricingPlanMessages.fixedPrice,
};

export const CAPPING_MAP = {
    [CAPPING.NONE]: pricingPlanMessages.none,
    [CAPPING.ONE_TIME]: pricingPlanMessages.oneTime,
    [CAPPING.REPEATING]: pricingPlanMessages.repeating,
};

export const CURRENCY_MAP = {
    [CURRENCY.BGN]: pricingPlanMessages.bgn,
    [CURRENCY.EUR]: pricingPlanMessages.eur,
};

export const FREE_TIME_TYPE_MAP = {
    [FREE_TIME_TYPE.CAPPED]: pricingPlanMessages.capped,
    [FREE_TIME_TYPE.EVERY_VISIT]: pricingPlanMessages.everyVisit,
};

export const TICK_TYPE_MAP = {
    [TICK_TYPE.MINUTE]: pricingPlanMessages.minute,
    [TICK_TYPE.HOUR]: pricingPlanMessages.hour,
    [TICK_TYPE.DAY]: pricingPlanMessages.day,
    [TICK_TYPE.WEEK]: pricingPlanMessages.week,
    [TICK_TYPE.MONTH]: pricingPlanMessages.month,
};

export const DAY_OF_WEEK_TRANSLATIONS_MAP = {
    [DAY_OF_WEEK.MONDAY]: workingHoursMessages.monday,
    [DAY_OF_WEEK.TUESDAY]: workingHoursMessages.tuesday,
    [DAY_OF_WEEK.WEDNESDAY]: workingHoursMessages.wednesday,
    [DAY_OF_WEEK.THURSDAY]: workingHoursMessages.thursday,
    [DAY_OF_WEEK.FRIDAY]: workingHoursMessages.friday,
    [DAY_OF_WEEK.SATURDAY]: workingHoursMessages.saturday,
    [DAY_OF_WEEK.SUNDAY]: workingHoursMessages.sunday,
};

export const paymentTypeBadgeOptions = {
    [PAYMENT_TYPE.CARD]: {
        label: pricingPlanMessages.card,
        theme: 'blue',
    },
    [PAYMENT_TYPE.CASH]: {
        label: pricingPlanMessages.cash,
        theme: 'purple',
    },
    [PAYMENT_TYPE.BANK]: {
        label: pricingPlanMessages.bank,
        theme: 'pink',
    },
    [PAYMENT_TYPE.WEB]: {
        label: pricingPlanMessages.web,
        theme: 'green',
    },
};

export const tickTypeBadgeOptions = {
    [TICK_TYPE.MINUTE]: {
        label: pricingPlanMessages.minute,
        theme: 'blue',
    },
    [TICK_TYPE.HOUR]: {
        label: pricingPlanMessages.hour,
        theme: 'pink',
    },
    [TICK_TYPE.DAY]: {
        label: pricingPlanMessages.day,
        theme: 'purple',
    },
    [TICK_TYPE.WEEK]: {
        label: pricingPlanMessages.week,
        theme: 'green',
    },
    [TICK_TYPE.MONTH]: {
        label: pricingPlanMessages.month,
        theme: 'yellow',
    },
};

export const usedInTypeBadgeOptions = {
    [USED_IN_TYPE.SITE]: {
        label: siteMessages.site,
        theme: 'blue',
    },
    [USED_IN_TYPE.ZONE]: {
        label: zoneMessages.zone,
        theme: 'pink',
    },
    [USED_IN_TYPE.GATEWAY]: {
        label: gatewayMessages.gateway,
        theme: 'purple',
    },
};

export const PAYMENT_TYPE_TRANSLATION_MAP = {
    [PAYMENT_TYPE.CARD]: pricingPlanMessages.card,
    [PAYMENT_TYPE.CASH]: pricingPlanMessages.cash,
    [PAYMENT_TYPE.BANK]: pricingPlanMessages.bank,
    [PAYMENT_TYPE.WEB]: pricingPlanMessages.web,
};